import React from 'react';
import { Alert, Button, Col, Form, Icon, Input, Radio, Row } from 'antd';
import InfoBox from '../../../components/InfoBox/InfoBox';
import PasswordRequirements from './PasswordRequirements';
import { ORG_ONBOARDING_INFO_COLOR } from '../onboardingConstants';

const FormItem = Form.Item;
const RadioGroup = Radio.Group;
const SPECIAL_CHARACTER = '~`!@#$%^&*()\\-_=+\\[\\]{};:\'",.<>/?|';

export default function AccountDetailsStep({
  form,
  initialValues,
  isSubmitting,
  isVerifyingEmail,
  emailFromLink,
  emailAlreadyRegistered,
  emailAlreadyRegisteredMessage,
  onClearEmailVerificationError,
  onGoToLogin,
  showBrokerPlanQuestion = false,
  brokerCreatingPlan,
  onContinue,
}) {
  const { getFieldDecorator, getFieldValue } = form;
  const passwordValue = getFieldValue('password') || '';
  const isBusy = isSubmitting || isVerifyingEmail;

  function checkPassword(rule, value, callback) {
    if (value && value !== getFieldValue('password')) {
      callback('Passwords do not match.');
      return;
    }

    callback();
  }

  function checkConfirm(rule, value, callback) {
    if (value && getFieldValue('confirmPassword')) form.validateFields(['confirmPassword'], { force: true });
    callback();
  }

  return (
    <div className="onboarding-registration-panel-body">
      <div className="onboarding-registration-panel-main">
        <InfoBox
          className="onboarding-registration-panel-infobox"
          color={ORG_ONBOARDING_INFO_COLOR}
          honorSectionGuidanceToggle={false}
          message={
            emailFromLink
              ? 'Your email was provided in the invitation link and cannot be changed here.'
              : 'If this license is for someone else, enter their email — they will receive the activation link.'
          }
        />

        {emailAlreadyRegistered ? (
          <Alert
            className="onboarding-email-registered-alert"
            type="warning"
            showIcon
            message="This email is already registered"
            description={
              <div className="onboarding-email-registered-alert-body">
                <p>
                  {emailAlreadyRegisteredMessage ||
                    'An account already exists for this email address. Please log in instead of creating a new account.'}
                </p>
                <Button type="primary" className="onboarding-email-registered-login-btn" onClick={onGoToLogin}>
                  Go to login
                </Button>
              </div>
            }
          />
        ) : null}

        <FormItem label="Email address">
          {getFieldDecorator('email', {
            initialValue: initialValues.email,
            rules: [
              { type: 'email', message: 'Please enter a valid email address.' },
              { required: true, message: 'Please enter the email address.' },
            ],
          })(
            <Input
              placeholder="you@example.com"
              disabled={isBusy || emailFromLink}
              readOnly={emailFromLink}
              spellCheck={false}
              onChange={() => onClearEmailVerificationError && onClearEmailVerificationError()}
            />,
          )}
        </FormItem>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <FormItem label="First name">
              {getFieldDecorator('firstName', {
                initialValue: initialValues.firstName,
                rules: [{ required: true, message: 'Please enter the first name.' }],
              })(<Input placeholder="First name" disabled={isBusy} />)}
            </FormItem>
          </Col>
          <Col xs={24} sm={12}>
            <FormItem label="Last name">
              {getFieldDecorator('lastName', {
                initialValue: initialValues.lastName,
                rules: [{ required: true, message: 'Please enter the last name.' }],
              })(<Input placeholder="Last name" disabled={isBusy} />)}
            </FormItem>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <FormItem label="Password" hasFeedback>
              {getFieldDecorator('password', {
                initialValue: initialValues.password,
                rules: [
                  { required: true, message: 'Please create a password.' },
                  {
                    pattern: RegExp(`^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[${SPECIAL_CHARACTER}])(?=.{6,})`),
                    message:
                      'Password must be at least 6 characters and contain a lowercase letter, uppercase letter, number and special character.',
                  },
                  { validator: checkConfirm },
                ],
              })(
                <Input
                  type="password"
                  placeholder="Create password"
                  disabled={isBusy}
                  autoComplete="new-password"
                />,
              )}
            </FormItem>
          </Col>
          <Col xs={24} sm={12}>
            <FormItem label="Confirm password" hasFeedback>
              {getFieldDecorator('confirmPassword', {
                initialValue: initialValues.confirmPassword,
                rules: [{ required: true, message: 'Please confirm the password.' }, { validator: checkPassword }],
              })(
                <Input
                  type="password"
                  placeholder="Repeat password"
                  disabled={isBusy}
                  autoComplete="new-password"
                />,
              )}
            </FormItem>
          </Col>
        </Row>

        <PasswordRequirements password={passwordValue} />

        {showBrokerPlanQuestion ? (
          <>
            <InfoBox
              className="onboarding-registration-panel-infobox"
              color={ORG_ONBOARDING_INFO_COLOR}
              honorSectionGuidanceToggle={false}
              message="Tell us whether you are setting up an organisation and plan now, or only creating your broker account."
            />
            <FormItem label="Are you creating a plan as part of this registration?">
              {getFieldDecorator('brokerCreatingPlan', {
                initialValue: brokerCreatingPlan === true ? 'yes' : brokerCreatingPlan === false ? 'no' : undefined,
                rules: [{ required: true, message: 'Please select an option.' }],
              })(
                <RadioGroup disabled={isBusy}>
                  <Radio value="yes">Yes — continue to organisation and plan setup</Radio>
                  <Radio value="no">No — I am only setting up my broker account</Radio>
                </RadioGroup>,
              )}
            </FormItem>
          </>
        ) : null}
      </div>

      <div className="onboarding-registration-actions onboarding-registration-actions-between onboarding-registration-actions-bar">
        <Button type="primary" htmlType="button" loading={isBusy} disabled={emailAlreadyRegistered} onClick={onContinue}>
          Continue <Icon type="arrow-right" />
        </Button>
      </div>
    </div>
  );
}
